import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ProductDetail, ProductListItem } from '../../../../types/ProductTypes';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import useProductQuery from '../../../../api/useProductQuery';
import useApi from '../../../../api/useApi';
import { useAppSettings } from '../../../../hooks/useAppSettings';
import { SectionCard, SectionTitle } from '../../Section.styles';
import {
    USCGComparisonSection,
    USCGLogoSection,
    USCGSearchSection,
    SearchControls,
    USCGSearchInput,
    USCGCheckButton,
    CompatibilityResult,
    USCGDocumentsSection,
    DocumentLink,
    USCGGroupInfo,
    USCGTable,
    SearchInputContainer,
    TypeaheadDropdown,
    TypeaheadItem,
    TypeaheadNoResults,
} from './USCGCompatibilitySection.styles';

interface USCGCompatibilitySectionProps {
    product: ProductDetail;
}

const USCGCompatibilitySection: React.FC<USCGCompatibilitySectionProps> = ({ product }) => {
    const [compareProduct, setCompareProduct] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductListItem | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchResults, setSearchResults] = useState<ProductListItem[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(false);
    const [compatibilityResult, setCompatibilityResult] = useState<{
        status: 'compatible' | 'incompatible' | 'restricted' | null;
        message: string;
        source?: string;
    }>({ status: null, message: 'Select a product to check compatibility' });

    const { downloadFile } = useDownloadFile();
    const { queryProducts } = useProductQuery();
    const { getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const queryProductsRef = useRef(queryProducts);

    // Keep queryProducts ref up to date
    useEffect(() => {
        queryProductsRef.current = queryProducts;
    }, [queryProducts]);

    // Minimum characters before showing typeahead
    const MIN_SEARCH_LENGTH = 3;
    const SEARCH_DEBOUNCE_MS = 300;

    // Search products from API with debounce
    const searchProductsDebounced = useCallback((searchTerm: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm.length < MIN_SEARCH_LENGTH) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        setIsSearching(true);
        setShowDropdown(true); // Show dropdown immediately with loading state
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                console.log('[USCG] Searching for:', searchTerm);
                const result = await queryProductsRef.current({
                    search: searchTerm,
                    pageSize: 10,
                    includeInactive: false,
                });
                console.log('[USCG] Search result:', result);
                setSearchResults(result.items);
            } catch (error) {
                console.error('[USCG] Error searching products:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    }, []); // No dependencies - uses ref

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCompareProduct(value);
        setSelectedProduct(null);
        setHighlightedIndex(-1);
        searchProductsDebounced(value);
    };

    const handleSelectProduct = (selectedItem: ProductListItem) => {
        setCompareProduct(selectedItem.tradeName);
        setSelectedProduct(selectedItem);
        setShowDropdown(false);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown || searchResults.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
                    handleSelectProduct(searchResults[highlightedIndex]);
                }
                break;
            case 'Escape':
                setShowDropdown(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    // Placeholder data - would normally come from API
    const compatibleGroups = [
        // Placeholder - would be loaded from API based on product's USCG group
    ];

    const handleCheckCompatibility = async () => {
        if (!selectedProduct) {
            setCompatibilityResult({
                status: null,
                message: 'Please select a product to compare',
            });
            return;
        }

        const apiUrl = appSettings?.api;
        if (!apiUrl) {
            setCompatibilityResult({
                status: null,
                message: 'API not configured',
            });
            return;
        }

        setIsCheckingCompatibility(true);
        setCompatibilityResult({
            status: null,
            message: 'Checking compatibility...',
        });

        try {
            // Call the USCG inter-product compatibility API
            const endpoint = `${apiUrl}/GetUscgInterProductCompatibility?id1=${product.id}&id2=${selectedProduct.id}`;
            console.log('[USCG] Checking compatibility:', endpoint);

            const response = await getApiData({ endpoint });
            console.log('[USCG] Compatibility response:', response);

            // Parse the response - adjust based on actual API response structure
            if (response) {
                const compatibility = response.compatibility || response.Compatibility || response;

                // Determine status based on response
                let status: 'compatible' | 'incompatible' | 'restricted' | null = null;
                let message = 'Unknown compatibility';

                if (typeof compatibility === 'string') {
                    const compLower = compatibility.toLowerCase();
                    if (compLower === 'compatible' || compLower === 'c' || compLower === '1') {
                        status = 'compatible';
                        message = 'Compatible';
                    } else if (compLower === 'incompatible' || compLower === 'i' || compLower === '0') {
                        status = 'incompatible';
                        message = 'Incompatible';
                    } else if (compLower === 'restricted' || compLower === 'r' || compLower === '2') {
                        status = 'restricted';
                        message = 'Restricted';
                    } else {
                        message = compatibility;
                    }
                } else if (typeof compatibility === 'number') {
                    if (compatibility === 1) {
                        status = 'compatible';
                        message = 'Compatible';
                    } else if (compatibility === 0) {
                        status = 'incompatible';
                        message = 'Incompatible';
                    } else if (compatibility === 2) {
                        status = 'restricted';
                        message = 'Restricted';
                    }
                }

                setCompatibilityResult({
                    status,
                    message,
                    source: 'Based on USCG Compatibility Chart',
                });
            } else {
                setCompatibilityResult({
                    status: null,
                    message: 'No compatibility data found',
                });
            }
        } catch (error: any) {
            console.error('[USCG] Error checking compatibility:', error);

            // Extract error message from API response
            const apiErrorMessage = error.response?.data?.message || error.response?.data || error.message;

            // Check if the error is about missing USCG data
            let userMessage = 'Failed to check compatibility';
            if (typeof apiErrorMessage === 'string') {
                if (
                    apiErrorMessage.toLowerCase().includes('uscg group') ||
                    apiErrorMessage.toLowerCase().includes('not exist') ||
                    apiErrorMessage.toLowerCase().includes('not found')
                ) {
                    userMessage = 'Product not found or missing USCG data!';
                } else {
                    userMessage = apiErrorMessage;
                }
            }

            setCompatibilityResult({
                status: null,
                message: userMessage,
            });
        } finally {
            setIsCheckingCompatibility(false);
        }
    };

    return (
        <SectionCard>
            <SectionTitle>USCG Compatibility</SectionTitle>

            {/* Product Comparison Tool */}
            <USCGComparisonSection>
                <USCGLogoSection>
                    <img src="/assets/images/uscg2.jpg" alt="USCG" />
                </USCGLogoSection>
                <USCGSearchSection>
                    <label>Product to be compared with:</label>
                    <SearchControls>
                        <SearchInputContainer>
                            <USCGSearchInput
                                ref={inputRef}
                                type="text"
                                placeholder="Search for product (min 3 characters)..."
                                value={compareProduct}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => {
                                    if (compareProduct.length >= MIN_SEARCH_LENGTH) {
                                        setShowDropdown(true);
                                    }
                                }}
                            />
                            {showDropdown && (
                                <TypeaheadDropdown ref={dropdownRef}>
                                    {isSearching ? (
                                        <TypeaheadNoResults>Searching...</TypeaheadNoResults>
                                    ) : searchResults.length > 0 ? (
                                        searchResults.map((item, index) => (
                                            <TypeaheadItem
                                                key={item.id}
                                                isHighlighted={index === highlightedIndex}
                                                onClick={() => handleSelectProduct(item)}
                                                onMouseEnter={() => setHighlightedIndex(index)}
                                            >
                                                <span className="product-name">{item.tradeName}</span>
                                                {item.categoryName && <span className="product-category">({item.categoryName})</span>}
                                            </TypeaheadItem>
                                        ))
                                    ) : (
                                        <TypeaheadNoResults>No products found</TypeaheadNoResults>
                                    )}
                                </TypeaheadDropdown>
                            )}
                        </SearchInputContainer>
                        <USCGCheckButton onClick={handleCheckCompatibility} disabled={!selectedProduct || isCheckingCompatibility}>
                            {isCheckingCompatibility ? 'Checking...' : 'Check Compatibility'}
                        </USCGCheckButton>
                    </SearchControls>
                    <CompatibilityResult className={compatibilityResult.status || ''}>
                        <h2>{compatibilityResult.message}</h2>
                        {compatibilityResult.source && <p>{compatibilityResult.source}</p>}
                    </CompatibilityResult>
                </USCGSearchSection>
            </USCGComparisonSection>

            {/* USCG Documents */}
            <USCGDocumentsSection>
                <DocumentLink>
                    <img src="/assets/images/USCG compatibility chart icon.png" alt="USCG compatibility chart" />
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            console.log('[USCG] Downloading USCG Compatibility Chart...');
                            downloadFile({ category: 'uscg', fileName: 'USCG Compatibility Chart with comments.xls' });
                        }}
                    >
                        USCG Compatibility Chart
                    </a>
                </DocumentLink>
                <DocumentLink>
                    <img src="/assets/images/USCG CFR Title 46 Part 150 icon.png" alt="CFR Title 46 Part 150" />
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            console.log('[USCG] Downloading CFR Title 46...');
                            downloadFile({ category: 'uscg', fileName: 'CFR Title 46 Part 150 - Compatibility of Cargoes.pdf' });
                        }}
                    >
                        CFR Title 46 Part 150 - Compatibility of Cargoes
                    </a>
                </DocumentLink>
            </USCGDocumentsSection>

            {/* Exceptions Section (Edit mode only - placeholder for now) */}
            {/* This would only show in edit mode
            <ExceptionsSection>
                <h3>Exceptions to the Compatibility Chart</h3>
                
                <ExceptionsTableContainer>
                    <h4>USCG Group Exceptions</h4>
                    <USCGTable>
                        <div className="table-header exceptions-header">
                            <span>USCG Group</span>
                            <span>Compatibility</span>
                        </div>
                        {uscgGroupExceptions.map((exception, index) => (
                            <div className="table-row exceptions-row" key={index}>
                                <span>{exception.uscgGroup.number} - {exception.uscgGroup.name}</span>
                                <span>{exception.compatibility}</span>
                            </div>
                        ))}
                    </USCGTable>
                </ExceptionsTableContainer>

                <ExceptionsTableContainer>
                    <h4>Inter-Product Exceptions</h4>
                    <USCGTable>
                        <div className="table-header inter-products-header">
                            <span>Product</span>
                            <span>USCG Group</span>
                            <span>Compatibility</span>
                        </div>
                        {interProductExceptions.map((exception, index) => (
                            <div className="table-row inter-products-row" key={index}>
                                <a href={`/product/${exception.productId}`}>{exception.productName}</a>
                                <span>{exception.uscgGroupNumber}</span>
                                <span>{exception.compatibility}</span>
                            </div>
                        ))}
                    </USCGTable>
                </ExceptionsTableContainer>
            </ExceptionsSection>
            */}

            {/* USCG Group Information */}
            {product.regulations?.uscgGroup && (
                <USCGGroupInfo>
                    <h3>Group Compatibilities</h3>
                    {compatibleGroups.length > 0 ? (
                        <>
                            <p>
                                The group{' '}
                                <strong>
                                    {product.regulations.uscgGroup.number} - {product.regulations.uscgGroup.name}
                                </strong>{' '}
                                is compatible with the following groups:
                            </p>
                            <USCGTable>
                                <div className="table-header">
                                    <span>USCG Group</span>
                                </div>
                                {compatibleGroups.map((group: any, index: number) => (
                                    <div className="table-row" key={index}>
                                        <span>
                                            {group.number} - {group.name}
                                        </span>
                                    </div>
                                ))}
                            </USCGTable>
                        </>
                    ) : (
                        <p>
                            The group{' '}
                            <strong>
                                {product.regulations.uscgGroup.number} - {product.regulations.uscgGroup.name}
                            </strong>{' '}
                            compatibility information will be loaded from the API.
                        </p>
                    )}
                </USCGGroupInfo>
            )}
        </SectionCard>
    );
};

export default USCGCompatibilitySection;
